import { ProductCartItem } from '__generated__/graphql';
import { useTranslate } from 'hooks/useTranslations';
import { Img } from 'ui/components/Img';
import { IComponentTypes, ComponentType } from 'sites/types';
import { isNotNullish } from 'utils/isNotNullish';
import { useSiteConfig } from 'hooks/useSiteConfig';

import {
  MINICART_PRICE_STRATEGY,
  ProductPrice,
} from '../ProductPrice/ProductPrice';
import { CartAppliedProductPromotions } from '../CartAppliedProductPromotions';
import { Price } from '../Price';

type IProductItem = {
  product: ProductCartItem;
  type?: IComponentTypes;
};

export const ProductItem: React.FC<IProductItem> = ({ product, type }) => {
  const translate = useTranslate();
  const {
    staticFeatures: { showHeaderSubHeaderProductDetail },
  } = useSiteConfig();
  const imageUrl =
    product.personalizationImageURL ||
    product.product?.imageUrl ||
    product.product?.images?.[0]?.href;

  const imageAlt =
    product.product?.images?.[0]?.alt ||
    `${product.product.name}, ${product.product.colorName}`;

  const isSecondaryType = ComponentType.SECONDARY == type;
  const isSalePriceElapsed = product?.product.productPrice?.isSalePriceElapsed;

  return (
    <div className="flex" data-test-id="product-item">
      <div className="w-32 mr-4">
        <Img src={imageUrl} alt={imageAlt} />
      </div>
      <div className="flex-grow">
        <div className="flex flex-col w-full">
          <h2
            className={`font-bold text-puma-black text-base leading-tight ${
              isSecondaryType ? 'mb-4' : ''
            }`}
            data-test-id="product-item-name"
          >
            {showHeaderSubHeaderProductDetail
              ? product.product.header
              : product.product.name}
          </h2>
          {showHeaderSubHeaderProductDetail && product.product.subHeader && (
            <span className="w-full text-base text-puma-gray-60 line-clamp-2">
              {product.product.subHeader}
            </span>
          )}
        </div>
        <CartAppliedProductPromotions product={product} className="my-4" />
        <div className="flex flex-wrap">
          <div className="w-full">
            <p className="flex items-center space-x-2">
              <span
                className="text-puma-black-300 text-xs sm:text-sm"
                data-test-id="product-item-color-label"
              >
                {translate('color')}:
              </span>
              <span
                className="text-xs sm:text-sm"
                data-test-id="product-item-color-name"
              >
                {product.product.colorName}
              </span>
            </p>
            <p className="flex items-center space-x-2">
              <span
                className="text-puma-black-300 text-xs sm:text-sm"
                data-test-id="product-item-size-label"
              >
                {translate('size')}:
              </span>
              <span
                className="text-xs sm:text-sm"
                data-test-id="product-item-size-name"
              >
                {product.product.size.label}
              </span>
            </p>
            {product?.personalizationName ? (
              <p className="flex items-center space-x-2">
                <span className="text-puma-black-300 text-xs sm:text-sm">
                  {translate('name')}:
                </span>
                <span className="text-xs sm:text-sm">
                  {product.personalizationName}
                </span>
              </p>
            ) : null}
            {product?.personalizationNumber ? (
              <p className="flex items-center space-x-2">
                <span className="text-puma-black-300 text-xs sm:text-sm">
                  {translate('number')}:
                </span>
                <span className="text-xs sm:text-sm">
                  {product.personalizationNumber}
                </span>
              </p>
            ) : null}
            {product.personalizationSurcharge && (
              <p className="flex items-center space-x-2">
                <span className="text-puma-black-300 text-xs sm:text-sm">
                  {translate('customizationCost')}:
                </span>
                <span className="text-xs sm:text-sm">
                  <Price amount={product.personalizationSurcharge} />
                </span>
              </p>
            )}
            {product.__typename === 'ProductCartItem' &&
              product.customizationName && (
                <p className="flex items-center space-x-2">
                  <span className="text-puma-black-300 text-xs sm:text-sm">
                    {translate('name')}:
                  </span>
                  <span className="text-xs sm:text-sm">
                    {product.customizationName}
                  </span>
                </p>
              )}
            {product.__typename === 'ProductCartItem' &&
              product.customizationNumber && (
                <p className="flex items-center space-x-2">
                  <span className="text-puma-black-300 text-xs sm:text-sm">
                    {translate('number')}:
                  </span>
                  <span className="text-xs sm:text-sm">
                    {product.customizationNumber}
                  </span>
                </p>
              )}
          </div>
          <div
            className={`flex flex-col font-bold w-full ${
              isSecondaryType ? 'mt-4' : ''
            }`}
          >
            <ProductPrice
              price={{
                amount: product.product.productPrice?.price,
                salePrice: product.product.productPrice?.salePrice,
                promotionPrice: product.product.productPrice?.promotionPrice,
                personalizationPrice: isNotNullish(product.personalizationPrice)
                  ? parseFloat(product.personalizationPrice)
                  : undefined,
                isSalePriceElapsed,
              }}
              compact
              {...MINICART_PRICE_STRATEGY}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
